import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Upload, Save, Edit2 } from 'lucide-react';

const BrandIdentity = () => {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    brand_name: '',
    logo_url: '',
    slogan: '',
    description: '',
    mission: '',
    vision: '',
    values: [],
    target_audience: '',
    personas: [],
    style: ''
  });
  const [newValue, setNewValue] = useState('');

  useEffect(() => {
    fetchBrandIdentity();
  }, []);

  const fetchBrandIdentity = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/brand-identity`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success && data.brand_identity) {
        setBrand(data.brand_identity);
        setFormData(data.brand_identity);
      } else {
        setEditing(true);
      }
    } catch (error) {
      console.error('Error fetching brand identity:', error);
      setEditing(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/brand-identity`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        }
      );
      const data = await response.json();
      if (data.success) {
        setBrand(data.brand_identity);
        setEditing(false);
      }
    } catch (error) {
      console.error('Error saving brand identity:', error);
    }
  };

  const addValue = () => {
    if (newValue.trim()) {
      setFormData({ ...formData, values: [...formData.values, newValue.trim()] });
      setNewValue('');
    }
  };

  const removeValue = (index) => {
    setFormData({ ...formData, values: formData.values.filter((_, i) => i !== index) });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Chargement de l'identité de marque...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Building2 className="inline mr-3 text-indigo-400" size={36} />
              Brand Identity
            </h1>
            <p className="text-slate-400 text-lg">Définissez l'identité de votre marque</p>
          </div>
          {!editing && brand && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setEditing(true)}
              className="px-6 py-3 bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/30 hover:bg-indigo-500/20 transition-all"
              data-testid="edit-brand-btn"
            >
              <Edit2 size={20} className="inline mr-2" />
              Modifier
            </motion.button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto">
        {editing ? (
          <form onSubmit={handleSave} className="space-y-6">
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-semibold text-white mb-6">Informations de base</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Nom de la marque *</label>
                  <input
                    type="text"
                    value={formData.brand_name}
                    onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    required
                    data-testid="brand-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Logo URL</label>
                  <input
                    type="url"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    placeholder="https://..."
                    data-testid="logo-url-input"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">Slogan</label>
                <input
                  type="text"
                  value={formData.slogan}
                  onChange={(e) => setFormData({ ...formData, slogan: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  placeholder="Votre slogan accrocheur..."
                  data-testid="slogan-input"
                />
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500 h-24"
                  placeholder="Décrivez votre marque en quelques phrases..."
                  data-testid="description-input"
                />
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-semibold text-white mb-6">Mission & Vision</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Mission</label>
                  <textarea
                    value={formData.mission}
                    onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500 h-24"
                    placeholder="Quelle est la mission de votre entreprise ?"
                    data-testid="mission-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Vision</label>
                  <textarea
                    value={formData.vision}
                    onChange={(e) => setFormData({ ...formData, vision: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500 h-24"
                    placeholder="Quelle est votre vision pour l'avenir ?"
                    data-testid="vision-input"
                  />
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-semibold text-white mb-6">Valeurs</h2>
              
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addValue())}
                  className="flex-1 px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  placeholder="Ajouter une valeur..."
                  data-testid="value-input"
                />
                <button
                  type="button"
                  onClick={addValue}
                  className="px-6 py-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-all"
                  data-testid="add-value-btn"
                >
                  Ajouter
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.values.map((value, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-indigo-500/20 text-indigo-300 rounded-xl border border-indigo-500/30 flex items-center gap-2"
                    data-testid={`value-${index}`}
                  >
                    {value}
                    <button
                      type="button"
                      onClick={() => removeValue(index)}
                      className="text-indigo-400 hover:text-indigo-300"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-semibold text-white mb-6">Audience</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Public cible</label>
                  <textarea
                    value={formData.target_audience}
                    onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500 h-24"
                    placeholder="Décrivez votre audience cible..."
                    data-testid="audience-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Style rédactionnel</label>
                  <input
                    type="text"
                    value={formData.style}
                    onChange={(e) => setFormData({ ...formData, style: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    placeholder="Ex: Professionnel, Inspirant, Humoristique..."
                    data-testid="style-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              {brand && (
                <button
                  type="button"
                  onClick={() => { setEditing(false); setFormData(brand); }}
                  className="flex-1 px-6 py-4 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-all font-semibold"
                >
                  Annuler
                </button>
              )}
              <button
                type="submit"
                className="flex-1 px-6 py-4 bg-gradient-to-r from-indigo-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-indigo-500/30 transition-all"
                data-testid="save-brand-btn"
              >
                <Save size={20} className="inline mr-2" />
                Enregistrer
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            {/* Display Mode */}
            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8">
              <div className="flex items-start gap-6">
                {brand.logo_url && (
                  <img src={brand.logo_url} alt="Logo" className="w-24 h-24 rounded-xl object-cover" />
                )}
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-white mb-2">{brand.brand_name}</h2>
                  {brand.slogan && <p className="text-xl text-indigo-300 mb-4">« {brand.slogan} »</p>}
                  {brand.description && <p className="text-slate-300">{brand.description}</p>}
                </div>
              </div>
            </div>

            {(brand.mission || brand.vision) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {brand.mission && (
                  <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-3">Mission</h3>
                    <p className="text-slate-300">{brand.mission}</p>
                  </div>
                )}
                {brand.vision && (
                  <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-3">Vision</h3>
                    <p className="text-slate-300">{brand.vision}</p>
                  </div>
                )}
              </div>
            )}

            {brand.values && brand.values.length > 0 && (
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Valeurs</h3>
                <div className="flex flex-wrap gap-3">
                  {brand.values.map((value, index) => (
                    <span key={index} className="px-4 py-2 bg-indigo-500/20 text-indigo-300 rounded-xl border border-indigo-500/30">
                      {value}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(brand.target_audience || brand.style) && (
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Audience & Style</h3>
                {brand.target_audience && (
                  <div className="mb-4">
                    <span className="text-sm font-medium text-slate-400">Public cible:</span>
                    <p className="text-slate-300 mt-1">{brand.target_audience}</p>
                  </div>
                )}
                {brand.style && (
                  <div>
                    <span className="text-sm font-medium text-slate-400">Style rédactionnel:</span>
                    <p className="text-slate-300 mt-1">{brand.style}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandIdentity;